import React, { useEffect, useMemo, useState } from 'react';

import cloneDeep from 'lodash/cloneDeep';
import { AxiosError } from 'axios';

import Modal from '../../../utils/ModalComponet';
import AlertWrapper from '../../../utils/AlertWrapper';
import { ACTIVITY_AVAILABLE_DAY_WEEKS } from '../../../../infrastructure/constants/activities';
import {
  Activity,
  ActivitySourceValue,
  BookingOption,
} from '../../../../types/Activity';
import { StripeAccount } from '../../../../types/Stripe';
import * as Service from '../../../../services/VendorsService';
import { isConnectedStripeAccount } from '../../ActivitySourceFields/helpers';
import {
  getIsNotIntegrationEnabled,
  hasNoBookingPlatformIntegration,
  isFareharborSource,
} from '../../../forms/EditPeekAndRedeamOrderForm/helpers';
import { PartyType } from '../../../../types/Party';

import ActivityCreateModalWrapper from './ActivityCreateModalWrapper';

/* eslint-disable no-shadow */

const initialFields = {
  tags: [],
  hours: ACTIVITY_AVAILABLE_DAY_WEEKS,
};

const getInitialData = (data: Partial<Activity>) => {
  if (!data) return initialFields;
  const { source, category = '', type } = data;
  const isNotIntegrationEnabled = getIsNotIntegrationEnabled(source);
  const isBookingPlatformIntegrationAbsent = hasNoBookingPlatformIntegration(
    source
  );
  const updatedActivity = data;
  updatedActivity.category = category;
  updatedActivity.type = type ?? '';
  if (isNotIntegrationEnabled) {
    updatedActivity.deadline = 72;
  }
  if (isBookingPlatformIntegrationAbsent) {
    updatedActivity.deadline = 0;
  }
  return updatedActivity;
};

const getInitialBookingOptionsData = data => {
  if (!data) return [];
  return data.bookingOptions;
};

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  allTags: string[];
  createActivity: (
    fields: Partial<Activity>,
    bookingOptionsData: BookingOption,
    activity: Activity | undefined
  ) => void;
  data: Partial<Activity>;
  existingSource?: ActivitySourceValue;
  showActivitySelector?: boolean;
  partyTypes: PartyType[];
};

const ActivityCreateModal = ({
  isOpen,
  closeModal,
  allTags,
  createActivity,
  data,
  existingSource,
  showActivitySelector,
  partyTypes,
}: Props) => {
  const [fieldsValue, setFieldsValue] = useState<Partial<Activity>>(
    getInitialData(data)
  );
  const {
    source,
    metadata,
    vendorId,
    title,
    commission,
    categoryId,
    city,
  } = fieldsValue;
  const isNotIntegrationEnabled = getIsNotIntegrationEnabled(source);
  const isBookingPlatformIntegrationAbsent = hasNoBookingPlatformIntegration(
    source
  );
  const { stripeId } = metadata ?? { stripeId: undefined };

  const hasRequiredStripeId = !!source && !!stripeId;

  const disable = useMemo(() => {
    return (
      !categoryId ||
      !title ||
      !city ||
      !vendorId ||
      (commission && commission > 100) ||
      typeof commission !== 'number' ||
      (isConnectedStripeAccount(fieldsValue.source) && !hasRequiredStripeId)
    );
  }, [
    categoryId,
    city,
    commission,
    fieldsValue.source,
    hasRequiredStripeId,
    title,
    vendorId,
  ]);

  const [bookingOptionsData, setBookingOptionsData] = useState(
    getInitialBookingOptionsData(data)
  );
  const [bookingOptionAddedAlert, setBookingOptionAddedAlert] = useState<
    string | null
  >(null);

  const [stripeAccounts, setStripeAccounts] = useState<StripeAccount[]>([]);

  const [selectedActivity, setSelectedActivity] = useState<
    Activity | undefined
  >();

  const onSave = event => {
    event.preventDefault();
    createActivity(fieldsValue, bookingOptionsData, selectedActivity);
  };

  const onChange = (key, value) => {
    setFieldsValue(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const onChangeCategory = (categoryId: string, subCategoryId: string) => {
    setFieldsValue(prevState => ({
      ...prevState,
      categoryId,
      subCategoryId,
    }));
  };

  const smoothCloseAlert = () => {
    setTimeout(() => {
      setBookingOptionAddedAlert(null);
    }, 2500);
  };

  const onAddBookingOption = (option, id) => {
    const data = cloneDeep(bookingOptionsData);
    if (id) {
      const index = data.findIndex(i => i.id === id);
      if (index === -1) return;
      data[index] = option;
    } else {
      data.push(option);
    }
    setBookingOptionsData(data);
    const alertText = `Booking Option was ${
      id ? 'updated' : 'added'
    }. Save the activity to keep changes`;
    setBookingOptionAddedAlert(alertText);
    smoothCloseAlert();
  };

  const onDeleteBookingOption = id => {
    let data = cloneDeep(bookingOptionsData);
    const index = data.findIndex(i => i.id === id);
    if (index === -1) return;
    data = data.filter(item => item.id !== id);
    setBookingOptionsData(data);
    setBookingOptionAddedAlert(
      'Booking Option was deleted. Save the activity to keep changes'
    );
    smoothCloseAlert();
  };

  useEffect(() => {
    let ignore = false;
    if (!vendorId) {
      return;
    }
    const getVendorStripeConnectedAccounts = async () => {
      try {
        const vendor = await Service.fetchVendorById(vendorId);
        const { stripeConnectedAccounts = [] } = vendor || {};
        if (!ignore) {
          setStripeAccounts(stripeConnectedAccounts);
        }
      } catch (e) {
        console.error(e);
        const error = e as AxiosError;
        alert(
          `Error code: ${error?.response?.status} \nError message: ${error?.response?.data}`
        );
      }
    };
    getVendorStripeConnectedAccounts();
    return () => {
      ignore = true;
    };
  }, [vendorId]);

  return (
    <>
      <AlertWrapper show={!!bookingOptionAddedAlert}>
        <p>{bookingOptionAddedAlert}</p>
      </AlertWrapper>
      <Modal
        open={isOpen}
        closeModal={closeModal}
        cancel={closeModal}
        action={onSave}
        actionButtonText={selectedActivity ? 'Update' : 'Create'}
        disabled={disable}
      >
        <ActivityCreateModalWrapper
          isNotIntegrationEnabled={isNotIntegrationEnabled}
          isBookingPlatformIntegrationAbsent={
            isBookingPlatformIntegrationAbsent
          }
          fieldsValue={fieldsValue}
          onChange={onChange}
          onChangeCategory={onChangeCategory}
          onFormSubmit={() => onSave}
          allTags={allTags}
          existingSource={existingSource}
          isFareharborSource={isFareharborSource(source)}
          stripeAccounts={stripeAccounts}
          bookingOptionsData={bookingOptionsData}
          // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
          // @ts-ignore
          onAddBookingOption={onAddBookingOption}
          showActivitySelector={showActivitySelector}
          onActivitySelect={setSelectedActivity}
          onDeleteBookingOption={onDeleteBookingOption}
          partyTypes={partyTypes}
        />
      </Modal>
    </>
  );
};

export default ActivityCreateModal;
